type LandingLocale = string;
import { automaticMobileFontSize, clampTypographySize } from './typography-scale.js';

export interface WebEditorInlineTextStyle {
  start: number;
  end: number;
  fontWeight?: number;
  fontSizeDesktop?: number;
  fontSizeMobile?: number;
  color?: string;
  highlight?: boolean;
}

export type WebEditorInlineTextStylePatch = Partial<
  Record<keyof Omit<WebEditorInlineTextStyle, 'start' | 'end'>, string | number | boolean | null>
>;

export type WebEditorInlineTextStyles = Partial<Record<LandingLocale, WebEditorInlineTextStyle[]>>;
export type WebEditorInlineTextSources = Partial<Record<LandingLocale, string>>;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const clampOffset = (value: number, textLength: number) => Math.min(textLength, Math.max(0, Math.round(value)));
const normalizeWeight = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(900, Math.max(100, Math.round(value / 100) * 100)) : undefined;

const sameStyle = (left: WebEditorInlineTextStyle, right: WebEditorInlineTextStyle) =>
  left.fontWeight === right.fontWeight &&
  left.fontSizeDesktop === right.fontSizeDesktop &&
  left.fontSizeMobile === right.fontSizeMobile &&
  left.color === right.color &&
  left.highlight === right.highlight;

const hasStyle = (style: WebEditorInlineTextStyle) =>
  style.fontWeight !== undefined ||
  style.fontSizeDesktop !== undefined ||
  style.fontSizeMobile !== undefined ||
  style.color !== undefined ||
  style.highlight === true;

export const normalizeInlineTextStyle = (
  value: unknown,
  textLength = Number.MAX_SAFE_INTEGER,
): WebEditorInlineTextStyle | null => {
  if (!isRecord(value) || typeof value.start !== 'number' || typeof value.end !== 'number') return null;
  const start = clampOffset(value.start, textLength);
  const end = clampOffset(value.end, textLength);
  const fontWeight = normalizeWeight(value.fontWeight);
  const fontSizeDesktop =
    typeof value.fontSizeDesktop === 'number' && Number.isFinite(value.fontSizeDesktop)
      ? clampTypographySize(value.fontSizeDesktop)
      : undefined;
  const fontSizeMobile =
    typeof value.fontSizeMobile === 'number' && Number.isFinite(value.fontSizeMobile)
      ? clampTypographySize(value.fontSizeMobile)
      : undefined;
  const color = typeof value.color === 'string' && /^#[0-9a-f]{6}$/i.test(value.color) ? value.color : undefined;
  const highlight = value.highlight === true ? true : undefined;
  const normalized: WebEditorInlineTextStyle = {
    start,
    end,
    ...(fontWeight !== undefined ? { fontWeight } : {}),
    ...(fontSizeDesktop !== undefined ? { fontSizeDesktop } : {}),
    ...(fontSizeMobile !== undefined ? { fontSizeMobile } : {}),
    ...(color !== undefined ? { color } : {}),
    ...(highlight ? { highlight } : {}),
  };
  return start < end && hasStyle(normalized) ? normalized : null;
};

export const patchInlineTextStyleRange = (
  existing: WebEditorInlineTextStyle[],
  start: number,
  end: number,
  patch: WebEditorInlineTextStylePatch,
  textLength: number,
) => {
  const rangeStart = clampOffset(Math.min(start, end), textLength);
  const rangeEnd = clampOffset(Math.max(start, end), textLength);
  const normalized = normalizeInlineTextStyleList(existing, textLength);
  if (rangeStart === rangeEnd) return normalized;

  const boundaries = new Set([rangeStart, rangeEnd]);
  normalized.forEach(entry => {
    if (entry.end <= rangeStart || entry.start >= rangeEnd) return;
    boundaries.add(Math.max(rangeStart, entry.start));
    boundaries.add(Math.min(rangeEnd, entry.end));
  });
  const offsets = [...boundaries].sort((left, right) => left - right);
  const outside = normalized.flatMap(entry => {
    if (entry.end <= rangeStart || entry.start >= rangeEnd) return [entry];
    return [
      ...(entry.start < rangeStart ? [{ ...entry, end: rangeStart }] : []),
      ...(entry.end > rangeEnd ? [{ ...entry, start: rangeEnd }] : []),
    ];
  });
  const inside = offsets.slice(0, -1).flatMap((segmentStart, index) => {
    const segmentEnd = offsets[index + 1];
    if (segmentEnd === undefined || segmentStart >= segmentEnd) return [];
    const current = normalized.find(entry => entry.start <= segmentStart && entry.end >= segmentEnd);
    const next = { ...(current ?? {}), start: segmentStart, end: segmentEnd } as WebEditorInlineTextStyle;
    for (const [key, value] of Object.entries(patch)) {
      if (value === null) delete next[key as keyof WebEditorInlineTextStyle];
      else Reflect.set(next, key, value);
    }
    return hasStyle(next) ? [next] : [];
  });
  return normalizeInlineTextStyleList([...outside, ...inside], textLength);
};

export const normalizeInlineTextStyleList = (value: unknown, textLength = Number.MAX_SAFE_INTEGER) => {
  const normalized = (Array.isArray(value) ? value : [])
    .map(entry => normalizeInlineTextStyle(entry, textLength))
    .filter((entry): entry is WebEditorInlineTextStyle => Boolean(entry))
    .sort((left, right) => left.start - right.start || left.end - right.end);
  return normalized.reduce<WebEditorInlineTextStyle[]>((result, entry) => {
    const previous = result[result.length - 1];
    if (previous && previous.end === entry.start && sameStyle(previous, entry)) previous.end = entry.end;
    else result.push({ ...entry });
    return result;
  }, []);
};

export const normalizeInlineTextStyles = (value: unknown): WebEditorInlineTextStyles => {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.keys(value)
      .map(locale => [locale, normalizeInlineTextStyleList(value[locale])] as const)
      .filter(([, entries]) => entries.length),
  );
};

export const normalizeInlineTextSources = (value: unknown): WebEditorInlineTextSources => {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.keys(value)
      .filter(locale => typeof value[locale] === 'string')
      .map(locale => [locale, value[locale] as string]),
  );
};

export const replaceInlineTextStyleRange = (
  existing: WebEditorInlineTextStyle[],
  start: number,
  end: number,
  style: Omit<WebEditorInlineTextStyle, 'start' | 'end'> | null,
  textLength: number,
) => {
  const rangeStart = clampOffset(Math.min(start, end), textLength);
  const rangeEnd = clampOffset(Math.max(start, end), textLength);
  if (rangeStart === rangeEnd) return normalizeInlineTextStyleList(existing, textLength);
  const preserved = normalizeInlineTextStyleList(existing, textLength).flatMap(entry => {
    if (entry.end <= rangeStart || entry.start >= rangeEnd) return [entry];
    return [
      ...(entry.start < rangeStart ? [{ ...entry, end: rangeStart }] : []),
      ...(entry.end > rangeEnd ? [{ ...entry, start: rangeEnd }] : []),
    ];
  });
  return normalizeInlineTextStyleList(
    style ? [...preserved, { start: rangeStart, end: rangeEnd, ...style }] : preserved,
    textLength,
  );
};

export const rebaseInlineTextStyles = (
  styles: WebEditorInlineTextStyle[],
  previousText: string,
  nextText: string,
) => {
  if (previousText === nextText) return normalizeInlineTextStyleList(styles, nextText.length);
  let prefix = 0;
  while (prefix < previousText.length && prefix < nextText.length && previousText[prefix] === nextText[prefix]) prefix += 1;
  let suffix = 0;
  while (
    suffix < previousText.length - prefix &&
    suffix < nextText.length - prefix &&
    previousText[previousText.length - 1 - suffix] === nextText[nextText.length - 1 - suffix]
  )
    suffix += 1;
  const previousChangeEnd = previousText.length - suffix;
  const delta = nextText.length - previousText.length;
  return normalizeInlineTextStyleList(
    styles.flatMap(entry => {
      if (entry.end <= prefix) return [entry];
      if (entry.start >= previousChangeEnd) return [{ ...entry, start: entry.start + delta, end: entry.end + delta }];
      return [];
    }),
    nextText.length,
  );
};

export const inlineStyleMobileSize = (style: WebEditorInlineTextStyle) =>
  style.fontSizeMobile ??
  (style.fontSizeDesktop === undefined ? undefined : automaticMobileFontSize(style.fontSizeDesktop));

const isWordCharacter = (value: string | undefined) => Boolean(value && /[\p{L}\p{N}]/u.test(value));

/** Maps an explicitly styled source range to a translation without pretending to understand words.
 * Relative offsets are snapped outwards to target-language word boundaries so a partial word is
 * never formatted. The editor communicates that this mapping is intentionally approximate. */
export const mapInlineTextStylesProportionally = (
  styles: WebEditorInlineTextStyle[],
  sourceText: string,
  targetText: string,
) => {
  if (!sourceText.length || !targetText.length) return [];
  return normalizeInlineTextStyleList(
    normalizeInlineTextStyleList(styles, sourceText.length).map(style => {
      let start = Math.round((style.start / sourceText.length) * targetText.length);
      let end = Math.round((style.end / sourceText.length) * targetText.length);
      while (start > 0 && start < targetText.length && isWordCharacter(targetText[start - 1]) && isWordCharacter(targetText[start])) start -= 1;
      while (end > 0 && end < targetText.length && isWordCharacter(targetText[end - 1]) && isWordCharacter(targetText[end])) end += 1;
      return { ...style, start, end };
    }),
    targetText.length,
  );
};
